import { BoundNumberField, BoundTextField, ButtonMenu, Css, FieldGroup, FormLines } from "@homebound/beam";
import { FieldState } from "@homebound/form-state";
import { InputMaybe } from "src/generated/graphqlTypes";
import { useReaction } from "src/hooks";
import { handleReasonNoteValidationRules } from "../utils";

type BoundMaterialLeadTimeConfigFieldsProps = {
  minLeadTimeInDays: FieldState<InputMaybe<number>>;
  expectedLeadTimeInDays: FieldState<InputMaybe<number>>;
  maxLeadTimeInDays: FieldState<InputMaybe<number>>;
  reasonNote: FieldState<InputMaybe<string>>;
  disabled?: boolean | string;
  deleteConfig?: {
    canDelete: boolean;
    onDelete: () => void;
  };
};

export function BoundMaterialLeadTimeConfigFields({
  minLeadTimeInDays,
  expectedLeadTimeInDays,
  maxLeadTimeInDays,
  reasonNote,
  disabled = false,
  deleteConfig,
}: BoundMaterialLeadTimeConfigFieldsProps) {
  // if any of the material lead time fields have changed, then clear the reason note and requrie it to be filled out
  const materialLeadTimeConfig = [minLeadTimeInDays, expectedLeadTimeInDays, maxLeadTimeInDays];

  useReaction(
    () => ({
      isDirty: materialLeadTimeConfig.map((field) => field.dirty).some((val) => val),
      reasonNoteValue: reasonNote.originalValue,
      hasExistingValues: materialLeadTimeConfig.map((field) => field.originalValue).some((val) => !!val),
    }),
    ({ isDirty, reasonNoteValue, hasExistingValues }) =>
      handleReasonNoteValidationRules(isDirty, reasonNote, reasonNoteValue, hasExistingValues),
    [reasonNote, materialLeadTimeConfig],
  );

  return (
    <FormLines width="full">
      {/* Only show delete button if we can delete the config */}
      {deleteConfig?.canDelete && (
        <div css={Css.df.jcfe.$}>
          <ButtonMenu
            trigger={{ icon: "verticalDots" }}
            items={[{ label: "Delete", onClick: deleteConfig.onDelete }]}
          />
        </div>
      )}
      <FieldGroup>
        <BoundNumberField
          label="Min Lead Time"
          type="days"
          field={minLeadTimeInDays}
          disabled={disabled}
          placeholder="7 days"
        />
        <BoundNumberField
          label="Max Lead Time"
          type="days"
          field={maxLeadTimeInDays}
          disabled={disabled}
          placeholder="7 days"
        />
        <BoundNumberField
          label="Expected Lead Time"
          type="days"
          field={expectedLeadTimeInDays}
          truncate
          disabled={disabled}
          placeholder="7 days"
        />
      </FieldGroup>
      <BoundTextField label="Update Note" field={reasonNote} disabled={disabled} />
    </FormLines>
  );
}
